import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet, Upload, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, KpiCard } from "@/components/KpiCard";
import { TelepassDialog } from "@/components/telepass/TelepassDialog";
import { TelepassImportDialog } from "@/components/telepass/TelepassImportDialog";
import { TelepassArchive, ArchiveTelepassDialog } from "@/components/telepass/TelepassArchive";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { stationColor } from "@/lib/format";
import { useSort, SortTh } from "@/lib/sort";

const FILTERS = [["all", "Tutti"], ["DISPONIBILE", "Disponibili"], ["NON_DISPONIBILE", "Non disponibili"], ["area_c", "Area C"]];

export default function TelepassPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("all");
  const [data, setData] = useState({ items: [], summary: {} });
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showImport, setShowImport] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archiving, setArchiving] = useState(null);

  const sid = isAdmin && stationId !== "all" ? stationId : undefined;
  const load = useCallback(() => { api.get("/telepass", { params: { station_id: sid } }).then((r) => setData(r.data)); }, [sid]);
  useEffect(load, [load]);
  useEffect(() => {
    api.get("/stations").then((r) => setStations(r.data));
    api.get("/fleet/vehicles").then((r) => setVehicles(r.data.vehicles));
  }, []);

  const items = useMemo(() => {
    const t = q.trim().toLowerCase().replace(/\s/g, "");
    return data.items.filter((i) => (filter === "all" || (filter === "area_c" ? i.area_c : i.status === filter))
      && (!t || [i.number, i.internal_number, i.plate, i.vehicle_ref, i.model, i.station_name, i.unavailable_reason].some((x) => (x || "").toLowerCase().replace(/\s/g, "").includes(t))));
  }, [data.items, q, filter]);
  const { sorted, sort, toggle } = useSort(items, { plate: (i) => i.plate || i.vehicle_ref });
  const S = (p) => <SortTh sort={sort} onSort={toggle} {...p} />;
  const taken = new Set(data.items.map((i) => i.vehicle_id).filter(Boolean));

  const remove = async (i) => {
    if (!window.confirm(`Eliminare il Telepass ${i.number}?`)) return;
    try { await api.delete(`/telepass/${i.id}`); toast.success("Telepass eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportXlsx = async () => {
    try {
      const { data: blob, headers } = await api.get("/telepass/export", { params: { station_id: sid }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "telepass.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const s = data.summary;
  const title = isAdmin ? (stationId === "all" ? "Telepass" : `Telepass ${stations.find((x) => x.id === stationId)?.name || ""}`) : `Telepass ${stations[0]?.name || ""}`;

  return (
    <div className="fade-up">
      <PageHeader title={title} subtitle="Dispositivi Telepass, stato, stazione, furgone associato e Area C.">
        <Button variant="outline" onClick={() => setShowArchive(true)} data-testid="telepass-archive-button"><Archive className="mr-2 h-4 w-4" /> Archivio</Button>
        <Button variant="outline" onClick={() => setShowImport(true)} data-testid="telepass-import-button"><Upload className="mr-2 h-4 w-4" /> Importa Excel</Button>
        <Button variant="outline" onClick={exportXlsx} data-testid="telepass-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button onClick={() => setEditing({ station_id: sid || "" })} data-testid="telepass-add-button"><Plus className="mr-2 h-4 w-4" /> Nuovo Telepass</Button>
      </PageHeader>

      {isAdmin && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="telepass-station-tabs">
          {[{ id: "all", name: "Tutte le stazioni" }, ...stations].map((st) => (
            <button key={st.id} onClick={() => setStationId(st.id)} data-testid={`telepass-tab-${st.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${stationId === st.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {st.id !== "all" && <span className="h-2 w-2 rounded-full" style={{ background: stationColor(stations, st.id) }} />}{st.name}
            </button>
          ))}
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiCard label="Telepass totali" value={s.total ?? 0} accent="#0F172A" testId="telepass-kpi-total" />
        <KpiCard label="Disponibili" value={s.available ?? 0} accent="#059669" testId="telepass-kpi-available" />
        <KpiCard label="Non disponibili" value={s.unavailable ?? 0} accent="#DC2626" testId="telepass-kpi-unavailable" />
        <KpiCard label="Associati a furgone" value={s.associated ?? 0} accent="#0284C7" testId="telepass-kpi-associated" />
        <KpiCard label="Area C" value={s.area_c ?? 0} accent="#D97706" testId="telepass-kpi-areac" />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-white p-1" data-testid="telepass-filters">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`telepass-filter-${k}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca n. Telepass, n. interno, targa..." className="pl-9" data-testid="telepass-search-input" />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="telepass-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
              <tr><S k="number">N. Telepass</S><S k="internal_number">N. interno</S><S k="status">Stato</S><S k="unavailable_reason">Non disponibile per</S><S k="station_name">Stazione</S>
                <S k="plate">Furgone</S><S k="area_c">Area C</S><S k="notes">Note</S><th className="px-3 py-2" /></tr>
            </thead>
            <tbody className="divide-y">
              {sorted.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50" data-testid={`telepass-row-${i.id}`}>
                  <td className="font-mono-num whitespace-nowrap px-3 py-2 font-semibold">{i.number}</td>
                  <td className="whitespace-nowrap px-3 py-2">{i.internal_number || "—"}</td>
                  <td className="px-3 py-2"><span className={`whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${i.status === "DISPONIBILE" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>{i.status === "DISPONIBILE" ? "Disponibile" : "Non disponibile"}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-600" data-testid={`telepass-reason-${i.id}`}>{i.unavailable_reason || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2">{i.station_name}</td>
                  <td className="whitespace-nowrap px-3 py-2" data-testid={`telepass-plate-${i.id}`}>{i.plate ? <><span className="font-mono-num rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs font-semibold">{i.plate}</span><span className="ml-2 text-xs text-slate-500">{i.model}</span></> : i.vehicle_ref ? <span className="font-mono-num text-xs text-slate-600" title="Non trovato in flotta">{i.vehicle_ref} <span className="font-sans text-amber-600">(non in flotta)</span></span> : <span className="text-slate-400">Non associato</span>}</td>
                  <td className="px-3 py-2" data-testid={`telepass-areac-${i.id}`}>{i.area_c ? <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">Sì</span> : <span className="text-slate-400">No</span>}</td>
                  <td className="max-w-xs px-3 py-2 text-xs text-slate-600"><p className="line-clamp-2">{i.notes || "—"}</p></td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`telepass-edit-${i.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-slate-500" title="Dismetti (sposta in archivio)" onClick={() => setArchiving(i)} data-testid={`telepass-dismiss-${i.id}`}><Archive className="h-4 w-4" /></Button>
                    {isAdmin && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(i)} data-testid={`telepass-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-500" data-testid="telepass-empty">{q || filter !== "all" ? "Nessun Telepass corrisponde ai filtri." : "Nessun Telepass registrato."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <TelepassArchive open={showArchive} stationId={sid} onClose={() => setShowArchive(false)} onChanged={load} />
      <ArchiveTelepassDialog item={archiving} onClose={() => setArchiving(null)} onDone={() => { setArchiving(null); load(); }} />
      <TelepassImportDialog key={String(showImport)} open={showImport} stations={stations} isAdmin={isAdmin} defaultStation={sid} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />
      <TelepassDialog item={editing} vehicles={vehicles} stations={stations} taken={taken} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
